import React from 'react';
import { CabeceraFija } from '../components/ui/CabeceraFija';
import { useIdioma } from '../i18n/idioma';
import { AlertTriangle, Download, FileText, Receipt } from 'lucide-react';
import { useProjects } from '../contexts/ProjectsContext';
import { useAuth } from '../contexts/AuthContext';
import { ProjectInvoice } from '../types';
import {
    facturasDelCliente, facturasPorPagar, facturasPagadas, saldoPendiente,
} from '../utils/clientInvoices';
import { formatCurrency } from '../utils/formatCurrency';
import { fechaCorta, fechaDeDia } from '../utils/fechas';
import { folioFactura } from '../utils/folioFactura';
import { ViewHeader } from '../components/ui/ViewHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { StatusDot } from '../components/ui/StatusDot';
import { SkeletonRows } from '../components/ui/Skeleton';
import { TableWrap, Table, THead, TBody, Th, Tr, Td, MoneyCell, ThActions, RowActions } from '../components/ui/Table';

/**
 * Facturación del portal del cliente.
 *
 * Antes esta pantalla estaba hecha con constantes fijas bajo un comentario
 * "Mock data for MVP": un saldo vencido de $1,250 y tres facturas inventadas
 * que se le enseñaban a CLIENTES REALES. No importaba ningún dato. Ahora lee
 * las facturas de los proyectos del cliente, igual que ClientDashboardView lee
 * sus proyectos y presupuestos.
 *
 * Regla que no se puede romper: las facturas en BORRADOR no salen de aquí. Un
 * borrador es el documento de trabajo del contratista — puede tener cifras a
 * medias o líneas de prueba. El cliente solo ve lo que se le emitió (`Sent`,
 * `Overdue`, `Paid`).
 */

/**
 * ── Las fechas de esta pantalla salen de `utils/fechas.ts` ──────────────────
 *
 * Aquí había un formateador propio: `new Date(iso).toLocaleDateString(locale)`.
 * Leía TODA fecha como un instante, y el vencimiento de una factura no lo es —
 * es un día del calendario. Con el taller en Las Vegas, el '2026-09-05' que
 * tecleó el contratista (guardado como medianoche UTC en las filas viejas) le
 * llegaba al dueño de la casa como «vence el 04 sep»: la factura del cliente
 * decía un día antes que la del contratista. Ese es exactamente el fallo que
 * `fechaDeDia` existe para cerrar — lee el día tal como se escribió, venga
 * `'2026-09-05'` o `'2026-09-05T00:00:00Z'`.
 *
 * El cobro (`paidAt`, `date`) SÍ es un instante, sellado con `new Date()` al
 * guardar, así que ése va por `fechaCorta`. Las dos son en cifras y a la
 * americana a propósito: lo explica la cabecera de `utils/fechas.ts`.
 */

/** Un hueco visible donde no hay fecha. `utils/fechas` devuelve cadena vacía. */
const oHueco = (texto: string): string => texto || '—';

const ClientBillingView: React.FC = () => {
    const { t } = useIdioma();
    const { projects, isLoadingProjects } = useProjects();
    const { clientId } = useAuth();

    /* La selección vive en utils/clientInvoices.ts, con pruebas: encierra la
       regla de que el BORRADOR nunca sale de la empresa, y esa no puede quedar
       suelta dentro de una vista donde el próximo cambio la borre sin querer. */
    const facturas = React.useMemo<ProjectInvoice[]>(
        () => facturasDelCliente(projects, clientId), [projects, clientId]);
    const porPagar = React.useMemo(() => facturasPorPagar(facturas), [facturas]);
    const pagadas = React.useMemo(() => facturasPagadas(facturas), [facturas]);
    const saldo = saldoPendiente(facturas);

    // Sin cuenta ligada no hay nada que mostrar; mismo texto que el panel.
    if (!clientId) {
        return (
            <div className="bg-white rounded-2xl border border-acero-200">
                <EmptyState
                    variant="error"
                    icon={<AlertTriangle size={18} />}
                    title={t('cliente.facturacion.errorcuenta.titulo')}
                    description={t('cliente.facturacion.errorcuenta.descripcion')}
                />
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-300">

            <CabeceraFija>

            <ViewHeader
                title={t('cliente.facturacion.titulo')}
                description={t('cliente.facturacion.descripcion')}
            />

            </CabeceraFija>

            {isLoadingProjects ? (
                /* Esqueleto y no "no tienes facturas": con mala señal, decirle a
                   alguien que no debe nada es peor que no decirle nada. */
                <div className="bg-white rounded-2xl border border-acero-200 p-6">
                    <SkeletonRows rows={4} />
                </div>
            ) : (
                <>
                    {/* Saldo y facturas por pagar */}
                    <div className="bg-white rounded-2xl border border-acero-200 overflow-hidden flex flex-col md:flex-row">
                        <div className="p-8 md:w-1/2 flex flex-col justify-center">
                            <p className="label-ui mb-2">{t('cliente.facturacion.saldo.titulo')}</p>
                            <p className="font-data tabular-nums text-4xl font-semibold text-navy-950">
                                {formatCurrency(saldo)}
                            </p>
                            {saldo > 0 && (
                                /* Sin botón de pago: no hay cobro en línea conectado.
                                   El que había abría un aviso de "función en
                                   desarrollo" — a un cliente que solo quiere pagar. */
                                <p className="text-[13px] text-acero-600 mt-4 leading-relaxed">
                                    {t('cliente.facturacion.saldo.comopagar')}
                                </p>
                            )}
                        </div>

                        <div className="bg-acero-50 p-8 md:w-1/2 border-t md:border-t-0 md:border-l border-acero-200">
                            <h2 className="label-ui mb-4">{t('cliente.facturacion.porpagar.titulo')}</h2>
                            {porPagar.length === 0 ? (
                                <p className="text-[13px] text-acero-500">
                                    {t('cliente.facturacion.porpagar.vacio')}
                                </p>
                            ) : (
                                <div className="space-y-2.5">
                                    {/* La clave lleva la posición: el `id` era el folio tecleado y
                                        dos facturas heredadas pueden compartirlo. */}
                                    {porPagar.map((inv, indice) => (
                                        <div key={`${inv.id}#${indice}`} className="flex items-center justify-between gap-3 bg-white p-3 rounded-xl border border-acero-200">
                                            <div className="min-w-0">
                                                <div className="text-[13px] font-medium text-navy-950 flex items-center gap-2">
                                                    <FileText size={14} className="text-acero-400 shrink-0" />
                                                    <span className="font-data tabular-nums truncate">{folioFactura(inv)}</span>
                                                </div>
                                                <div className="text-xs text-acero-500 mt-0.5 font-data tabular-nums">
                                                    {t('facturas.vence', { fecha: oHueco(fechaDeDia(inv.dueDate)) })}
                                                </div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <div className="font-data tabular-nums text-[13px] font-semibold text-navy-950">
                                                    {formatCurrency(inv.amount)}
                                                </div>
                                                <StatusDot
                                                    tone={inv.status === 'Overdue' ? 'peligro' : 'aviso'}
                                                    className="mt-0.5 !text-xs"
                                                >
                                                    {inv.status}
                                                </StatusDot>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Historial */}
                    <div className="bg-white rounded-2xl border border-acero-200 overflow-hidden">
                        <div className="px-5 py-4 border-b border-acero-200">
                            <h2 className="text-base font-semibold tracking-tight text-navy-950">{t('cliente.facturacion.historial.titulo')}</h2>
                        </div>

                        {pagadas.length === 0 ? (
                            <EmptyState
                                icon={<Receipt size={18} />}
                                title={t('cliente.facturacion.historial.vacio.titulo')}
                                description={t('cliente.facturacion.historial.vacio.descripcion')}
                            />
                        ) : (
                            <TableWrap className="px-4">
                                <Table>
                                    <THead>
                                        <Th>{t('cliente.facturacion.tabla.factura')}</Th>
                                        <Th hideOnMobile>{t('cliente.facturacion.tabla.fecha')}</Th>
                                        <Th>{t('cliente.facturacion.tabla.estado')}</Th>
                                        <Th align="right">{t('cliente.facturacion.tabla.monto')}</Th>
                                        <ThActions label={t('cliente.facturacion.tabla.recibo')} />
                                    </THead>
                                    <TBody>
                                        {pagadas.map((inv, indice) => (
                                            <Tr key={`${inv.id}#${indice}`}>
                                                <Td primary>
                                                    <span className="flex items-center gap-2">
                                                        <FileText size={14} className="text-acero-400 shrink-0" />
                                                        <span className="font-data tabular-nums">{folioFactura(inv)}</span>
                                                    </span>
                                                </Td>
                                                <Td hideOnMobile muted className="font-data tabular-nums">
                                                    {oHueco(fechaCorta(inv.paidAt || inv.date))}
                                                </Td>
                                                <Td>
                                                    <StatusDot tone="exito">{t('cliente.facturacion.tabla.pagada')}</StatusDot>
                                                </Td>
                                                <MoneyCell value={inv.amount} format={formatCurrency} />
                                                <Td align="right">
                                                    <RowActions>
                                                        <button
                                                            type="button"
                                                            aria-label={t('facturas.descargarRecibo', { id: folioFactura(inv) })}
                                                            className="p-2 text-acero-500 hover:text-navy-950 hover:bg-acero-100 rounded-lg transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                                                        >
                                                            <Download size={16} />
                                                        </button>
                                                    </RowActions>
                                                </Td>
                                            </Tr>
                                        ))}
                                    </TBody>
                                </Table>
                            </TableWrap>
                        )}
                    </div>
                </>
            )}

        </div>
    );
};

export default ClientBillingView;
